import React from "react";
import { Link } from "react-router-dom";
import { paths } from "../../../routes/paths";
import { useHasPermission } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetCollectionsSummaryQuery } from "../../collections/collectionsApi";
import { useDashboardPeriod } from "../useDashboardPeriod";
import { money, percentChange } from "../dashboardHelpers";
import { BRAND } from "../chartColors";
import DashboardSectionCard from "../components/DashboardSectionCard";

// Driven by the global Period/Compare control (previously hardcoded to
// "This Month") - a ranking movement indicator per client only when a
// single-window comparison is selected (a multi-year overlay doesn't
// translate into a clean per-client ranking-delta, so it's intentionally
// not attempted here - see the feasibility report's own "don't force
// comparison where it adds noise" guidance).
const TopClientsByCollectionCard = () => {
  const canView = useHasPermission("Collections.View");
  const { selectedBranchId } = useBranchFilter();
  const { period, comparison } = useDashboardPeriod();
  const branchId = selectedBranchId ?? undefined;
  const singleCompare = comparison?.type === "single" ? comparison : null;

  const { data, isFetching } = useGetCollectionsSummaryQuery(
    { branchId, dateFrom: period.dateFrom, dateTo: period.dateTo, groupBy: "customer" },
    { skip: !canView }
  );
  const { data: compareData } = useGetCollectionsSummaryQuery(
    singleCompare ? { branchId, dateFrom: singleCompare.dateFrom, dateTo: singleCompare.dateTo, groupBy: "customer" } : undefined,
    { skip: !canView || !singleCompare }
  );

  if (!canView) return null;

  const topClients = (data || []).slice().sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 5);
  const max = Math.max(1, ...topClients.map((c) => c.totalAmount));
  const compareByKey = new Map((compareData || []).map((c) => [c.groupKey, c.totalAmount]));

  return (
    <div className="col-12 col-xl-6">
      <DashboardSectionCard title="Top Clients by Collection" headerRight={period.label} isLoading={isFetching} isEmpty={topClients.length === 0}>
        <ul className="list-unstyled mb-0">
          {topClients.map((c) => {
            const compareAmount = singleCompare ? compareByKey.get(c.groupKey) : undefined;
            const change = singleCompare ? percentChange(c.totalAmount, compareAmount) : null;
            return (
              <li key={c.groupKey} className="mb-2">
                <div className="d-flex justify-content-between small mb-1">
                  {/* groupKey is the customer id - /collections reads ?custId=,
                      so this opens that client's own receipts for the period. */}
                  <Link
                    to={paths.collectionsFiltered({ branchId, custId: c.groupKey, custName: c.groupLabel, dateFrom: period.dateFrom, dateTo: period.dateTo })}
                    className="fw-semibold text-decoration-none"
                    title={`See ${c.groupLabel}'s collections for ${period.label}`}
                  >
                    {c.groupLabel}
                  </Link>
                  <span>
                    {money(c.totalAmount)}
                    {singleCompare && change !== null && (
                      <span className={`ms-2 ${change >= 0 ? "text-success" : "text-danger"}`}>
                        {change >= 0 ? "↑" : "↓"} {Math.abs(change).toFixed(0)}%
                      </span>
                    )}
                    {singleCompare && change === null && (
                      <span className="ms-2 text-muted fst-italic">new</span>
                    )}
                  </span>
                </div>
                <div className="progress" style={{ height: 6 }}>
                  <div className="progress-bar" style={{ width: `${(c.totalAmount / max) * 100}%`, backgroundColor: BRAND }} />
                </div>
              </li>
            );
          })}
        </ul>
      </DashboardSectionCard>
    </div>
  );
};

export default TopClientsByCollectionCard;
